import { apiClient } from '@/api/client';
import { useAuth } from '@/lib/auth';
import { useQuery } from '@tanstack/react-query';

export interface EntityLog {
  id: number;
  eventType: string;
  properties: Record<string, unknown>;
  createdAt: string;
  user?: {
    id: number;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export interface EntityLogsResult {
  logs: EntityLog[];
  total: number;
  totalPages: number;
  currentPage: number;
}

// Human-readable event labels
const EVENT_LABELS: Record<string, string> = {
  // Admin - Business
  admin_business_viewed: 'Viewed',
  admin_business_updated: 'Updated',
  admin_business_status_changed: 'Status changed',
  admin_business_deleted: 'Deleted',
  admin_business_refreshed: 'Synced from external source',
  // Admin - User
  admin_user_viewed: 'Viewed',
  admin_user_updated: 'Updated',
  admin_user_status_changed: 'Enabled/Disabled',
  admin_user_deleted: 'Deleted',
  admin_user_role_changed: 'Role changed',
  // Admin - Review
  admin_review_visibility_changed: 'Review visibility changed',
  admin_review_score_inclusion_changed: 'Review score inclusion changed',
  // Admin - Import Job
  admin_import_job_created: 'Import job created',
  admin_import_job_paused: 'Import job paused',
  admin_import_job_resumed: 'Import job resumed',
  admin_import_job_cancelled: 'Import job cancelled',
  admin_import_job_completed: 'Import job completed',
  admin_import_job_deleted: 'Import job deleted',
  admin_import_job_retry: 'Retried failed items',
  // Admin - Scores
  admin_score_recalculated: 'Score recalculated',
  admin_score_batch_recalculated: 'Batch score recalculation queued',
  // Client events (also shown)
  business_created: 'Created',
  business_viewed: 'Viewed (mobile)',
  business_favorited: 'Favorited',
  business_unfavorited: 'Unfavorited',
  review_submitted: 'Review submitted',
  user_registered: 'Registered',
  plan_created: 'Plan created',
  plan_deleted: 'Plan deleted',
};

export const getEventLabel = (eventType: string): string =>
  EVENT_LABELS[eventType] || eventType.replace(/_/g, ' ');

export const useEntityLogs = (
  entityType: 'business' | 'user' | 'import_job' | 'review' | 'plan',
  entityId: number | undefined,
  options?: { page?: number; limit?: number; excludeViews?: boolean },
) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const page = options?.page || 1;
  const limit = options?.limit || 20;
  const excludeViews = options?.excludeViews ?? true;

  return useQuery({
    queryKey: ['entity-logs', entityType, entityId, page, limit, excludeViews],
    queryFn: async (): Promise<EntityLogsResult> => {
      // Use type assertion for dynamic endpoint
      const response = await (apiClient.GET as Function)(
        `/admin/entity-logs/${entityType}/${entityId}`,
        {
          params: { query: { page, limit, excludeViews } },
        },
      );

      if (response.error) {
        throw new Error('Failed to fetch entity logs');
      }

      const headers = response.response.headers;
      return {
        logs: (response.data || []) as EntityLog[],
        total: parseInt(headers.get('X-Total-Count') || '0'),
        totalPages: parseInt(headers.get('X-Total-Pages') || '1'),
        currentPage: parseInt(headers.get('X-Current-Page') || '1'),
      };
    },
    enabled: isAuthenticated && !isAuthLoading && !!entityId,
  });
};
